import { useEffect, useState } from 'react';

import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';

import FeatureGates from '@atlaskit/feature-gate-js-client';
// eslint-disable-next-line no-restricted-imports
import { HelpPageAri } from '@atlassian/ari';
import { getCloudId, getHelpCenterAri } from '@helpcenter/meta';

import type { useIsTopicPageQuery } from './__generated__/useIsTopicPageQuery.graphql';

const TopicPagesQuery = graphql`
	query useIsTopicPageQuery($cloudId: ID!, $hubAri: ID!, $isDraft: Boolean) {
		helpCenter(cloudId: $cloudId) {
			helpCenterById(helpCenterAri: $hubAri, isDraft: $isDraft) @optIn(to: "HelpCenterAggBeta") {
				__typename
				... on HelpCenter {
					topicPages: pages(filter: { ids: [], type: TOPIC }) {
						id
					}
				}
			}
		}
	}
`;

// Same gate the navbar's Topics menu and the editor's Topics panel read: with it
// off a hub has no topic pages, so there is nothing to look up.
const HUBS_TOPICS_GATE = 'hubs_premium_upsell';
const isTopicsEnabled = (): boolean => FeatureGates.checkGate(HUBS_TOPICS_GATE);

const EMPTY_TOPIC_PAGE_IDS: readonly string[] = [];

const getPageIdFromAri = (pageAri: string): string | null => {
	try {
		return HelpPageAri.parse(pageAri).pageId;
	} catch {
		return null;
	}
};

/**
 * Whether the hub page currently being viewed is a topic page rather than an
 * ordinary custom page.
 *
 * Both are served by `/page/:id`, and in view mode neither the route nor
 * `HubContext` carries the page's type, so the hub's topic pages are fetched
 * and the route's page id matched against them. Answered from the Relay store
 * whenever possible: the navbar's own `navLinksQuery` has already read the same
 * `pages(filter: { ids: [], type: TOPIC })` field with the same arguments, so
 * `store-or-network` normally resolves without a second round trip.
 *
 * Pass no page id for any page that cannot be a topic page to skip the lookup.
 * Gated on `hubs_premium_upsell`: with the gate off nothing is fetched and every
 * page of this route keeps the custom page label it has today.
 */
export const useIsTopicPage = (pageId?: string | null): boolean => {
	const environment = useRelayEnvironment();
	const [topicPageIds, setTopicPageIds] = useState<readonly string[]>(EMPTY_TOPIC_PAGE_IDS);
	const cloudId = getCloudId();
	const helpCenterAri = getHelpCenterAri();

	useEffect(() => {
		if (!pageId || !cloudId || !helpCenterAri || !isTopicsEnabled()) {
			return;
		}

		const subscription = fetchQuery<useIsTopicPageQuery>(
			environment,
			TopicPagesQuery,
			// View mode always renders published content, and passing `isDraft`
			// explicitly keeps the arguments identical to `navLinksQuery`'s, which
			// is what lets this resolve from the store.
			{ cloudId, hubAri: helpCenterAri, isDraft: false },
			{ fetchPolicy: 'store-or-network' },
		).subscribe({
			next: (data) => {
				const helpCenterById = data.helpCenter?.helpCenterById;

				if (helpCenterById?.__typename !== 'HelpCenter') {
					return;
				}

				setTopicPageIds(
					(helpCenterById.topicPages ?? [])
						.map((page) => (page?.id ? getPageIdFromAri(page.id) : null))
						.filter((id): id is string => id !== null),
				);
			},
			// Nothing to recover: the menu keeps the custom page label, which is
			// what it shows for every page of this route today.
			error: () => setTopicPageIds(EMPTY_TOPIC_PAGE_IDS),
		});

		return () => subscription.unsubscribe();
	}, [cloudId, environment, helpCenterAri, pageId]);

	return !!pageId && topicPageIds.includes(pageId);
};
